import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import DescribedField from '../DescribedField';
import servicesService from '../../../services/servicesService';

/**
 * ServicesBlockEditor — Editor do bloco Serviços
 *
 * A página NÃO guarda mais cópia de serviço. Ela exibe automaticamente todos os serviços ativos do
 * cadastro, lidos ao vivo do catálogo no momento em que o visitante abre a página.
 *
 * Por que a seleção deixou de existir: o bloco guardava uma cópia congelada no dia da publicação, e
 * nada a atualizava. Medido em produção (01/09/2026): 66 serviços já excluídos continuavam
 * anunciados, 30 com nome trocado e 16 com PREÇO diferente do cobrado — e preço anunciado é oferta
 * que vincula quem anuncia. Manter a seleção significaria manter a cópia, e com ela o defeito.
 * Ver docs/ANALISE-REFERENCIAS-DINAMICAS-NAS-PAGINAS.md §7.
 *
 * O que sobra aqui é a única decisão que é da PÁGINA e não do cadastro: exibir ou não o preço.
 * Ela vale para o bloco inteiro — não existe "showPrice" no cadastro do serviço, então esse campo
 * não duplica nada.
 *
 * Props:
 *   - value: { showPrice?: boolean }
 *   - onChange: (patch) => void
 *   - context: { availableServices, ... }
 */
const ServicesBlockEditor = ({ value = {}, onChange, context = {} }) => {
  const [allServices, setAllServices] = useState(context.availableServices ?? null);
  const [loadError, setLoadError] = useState(false);

  // Páginas publicadas antes desta mudança ainda trazem `items` com o showPrice por serviço.
  // Enquanto a limpeza do banco não roda, a intenção do dono é lida dali — de forma CONSERVADORA:
  // se ALGUM item estava escondido, o bloco esconde. Revelar um preço que o dono decidiu esconder
  // é irreversível (já foi ao ar); esconder um que ele mostrava, ele religa aqui em dois cliques.
  // Mesma regra do renderizador, para o editor não divergir do site.
  const showPrice = typeof value.showPrice === 'boolean'
    ? value.showPrice
    : !(Array.isArray(value.items) && value.items.some((it) => it && it.showPrice === false));

  useEffect(() => {
    if (allServices !== null) return;
    servicesService.getAll()
      .then((data) => {
        const arr = Array.isArray(data) ? data : (data?.$values || []);
        setAllServices(arr);
      })
      .catch(() => {
        setAllServices([]);
        setLoadError(true);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (context.availableServices) setAllServices(context.availableServices);
  }, [context.availableServices]);

  const handleShowPrice = (next) => {
    // Grava `showPrice` no bloco e descarta `items`: manter a cópia aqui a faria voltar ao banco no
    // próximo salvar, desfazendo em silêncio o que a migração limpou.
    const { items, ...rest } = value; // eslint-disable-line no-unused-vars
    onChange({ ...rest, showPrice: next });
  };

  if (allServices === null) {
    return (
      <DescribedField label="Serviços" description="Carregando seu catálogo…">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      </DescribedField>
    );
  }

  const ativos = allServices.filter((s) => s?.isActive !== false).length;

  return (
    <DescribedField
      label="Serviços exibidos"
      description="Todos os serviços do seu cadastro aparecem nesta página automaticamente, sempre com o nome, a duração e o preço atuais. Para mudar o que aparece, edite o cadastro de serviços."
      helperLink={{ entity: 'service' }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {loadError && (
          <Alert severity="warning">
            Não foi possível carregar seus serviços agora. Isso não afeta a página — ela lê o
            catálogo diretamente.
          </Alert>
        )}

        {!loadError && ativos === 0 && (
          <Alert severity="info">
            Nenhum serviço cadastrado. Cadastre seus serviços para que apareçam nesta página.
          </Alert>
        )}

        {!loadError && ativos > 0 && (
          <Typography variant="caption" color="text.secondary">
            {ativos} serviço{ativos !== 1 ? 's' : ''} será
            {ativos !== 1 ? 'ão' : ''} exibido{ativos !== 1 ? 's' : ''} nesta página.
          </Typography>
        )}

        <FormControl size="small" sx={{ maxWidth: 260 }}>
          <InputLabel id="services-block-price-visibility">Preço</InputLabel>
          <Select
            labelId="services-block-price-visibility"
            value={showPrice ? 'show' : 'hide'}
            label="Preço"
            onChange={(e) => handleShowPrice(e.target.value === 'show')}
          >
            <MenuItem value="show">Exibir preço</MenuItem>
            <MenuItem value="hide">Ocultar preço</MenuItem>
          </Select>
        </FormControl>

        <Typography variant="caption" color="text.secondary">
          Serviço sem preço cadastrado não mostra valor na página, mesmo com &quot;Exibir preço&quot;.
        </Typography>
      </Box>
    </DescribedField>
  );
};

ServicesBlockEditor.propTypes = {
  value: PropTypes.shape({
    showPrice: PropTypes.bool,
    // `items` só existe em páginas publicadas antes da migração; é lido para herdar a intenção do
    // dono e nunca é gravado de volta.
    items: PropTypes.array,
  }),
  onChange: PropTypes.func.isRequired,
  context: PropTypes.object,
};

export default ServicesBlockEditor;
